<template>
  <app-content class="h-panel">
    <div class="h-panel-bar">
      <span class="h-panel-title">科目余额表</span>
    </div>
    <div class="margin-right-left margin-top">
      <account-period-selector
          @confirm="handleConfirm"
          @date-change="handleDateChange"
          :current-subject="subject"
          :subject-list="subjectList"
      />
      <div class="float-right" style="display:flex;gap:12px;align-items:center;">
        <Button @click="exportExcel">导出</Button>
        <Checkbox v-model="showNumPrice">显示数量金额</Checkbox>
      </div>
    </div>
    <div class="h-panel-body" type="flex" :space-x="10">
      <!-- 外层容器：控制滚动（与科目汇总风格一致） -->
      <div style="max-height: 670px; overflow-y: auto; width: 100%;">
        <Loading text="数据加载中..." :loading="loading"></Loading>

        <!-- 非数量金额表格：单表格结构 + 优化边框重叠效果（模仿科目汇总） -->
        <table class="balance" v-if="!showNumPrice" style="border-collapse: separate; border-spacing: 0;">
          <thead class="header" style="position: sticky; top: 0; z-index: 1;">
          <tr>
            <td rowspan="2" v-width="120">科目编码</td>
            <td rowspan="2" v-width="200">科目名称</td>
            <td colspan="2">期初余额</td>
            <td colspan="2">本期发生额</td>
            <td colspan="2">期末余额</td>
          </tr>
          <tr>
            <td v-width="100">借方</td>
            <td v-width="100">贷方</td>
            <td v-width="100">借方</td>
            <td v-width="100">贷方</td>
            <td v-width="100">借方</td>
            <td v-width="100">贷方</td>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in dataList" :key="item.subjectId">
            <td :style="{'padding-left':(item.level)*10+'px'}">{{item.code}}</td>
            <td :style="{'padding-left':(item.level)*10+'px'}"><span class="text-hover">{{item.name}}</span></td>
            <td align="right">{{item.beginningDebitBalance|numFormat}}</td>
            <td align="right">{{item.beginningCreditBalance|numFormat}}</td>
            <td align="right">{{item.currentDebitAmount|numFormat}}</td>
            <td align="right">{{item.currentCreditAmount|numFormat}}</td>
            <td align="right" :class="{tip:item.endingDebitBalance<0}">{{item.endingDebitBalance|numFormat}}</td>
            <td align="right" :class="{tip:item.endingCreditBalance<0}">{{item.endingCreditBalance|numFormat}}</td>
          </tr>
          <tr v-if="!dataList.length">
            <td colspan="8" class="text-center padding">暂无数据</td>
          </tr>
          </tbody>
        </table>

        <!-- 数量金额表格：单表格结构 + 优化边框重叠效果（模仿科目汇总） -->
        <table class="balance" v-else style="border-collapse: separate; border-spacing: 0;">
          <thead class="header" style="position: sticky; top: 0; z-index: 1;">
          <tr>
            <td rowspan="2" v-width="120">科目编码</td>
            <td rowspan="2" v-width="200">科目名称</td>
            <td rowspan="2" v-width="100">计量单位</td>
            <td colspan="4">期初余额</td>
            <td colspan="4">本期发生额</td>
            <td colspan="4">期末余额</td>
          </tr>
          <tr>
            <td v-width="100">借方数量</td>
            <td v-width="100">借方金额</td>
            <td v-width="100">贷方数量</td>
            <td v-width="100">贷方金额</td>
            <td v-width="100">借方数量</td>
            <td v-width="100">借方金额</td>
            <td v-width="100">贷方数量</td>
            <td v-width="100">贷方金额</td>
            <td v-width="100">借方数量</td>
            <td v-width="100">借方金额</td>
            <td v-width="100">贷方数量</td>
            <td v-width="100">贷方金额</td>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in dataList" :key="item.subjectId">
            <td :style="{'padding-left':(item.level)*10+'px'}">{{item.code}}</td>
            <td :style="{'padding-left':(item.level)*10+'px'}"><span class="text-hover">{{item.name}}</span></td>
            <td class="text-center">{{item.unit || '-'}}</td>
            <td align="right">&nbsp;</td>
            <td align="right">{{item.beginningDebitBalance|numFormat}}</td>
            <td align="right">&nbsp;</td>
            <td align="right">{{item.beginningCreditBalance|numFormat}}</td>
            <td align="right">{{item.currentDebitAmountNum|numFormat}}</td>
            <td align="right">{{item.currentDebitAmount|numFormat}}</td>
            <td align="right">{{item.currentCreditAmountNum|numFormat}}</td>
            <td align="right">{{item.currentCreditAmount|numFormat}}</td>
            <td align="right">{{item.endingDebitBalanceNum|numFormat}}</td>
            <td align="right">{{item.endingDebitBalance|numFormat}}</td>
            <td align="right">{{item.endingCreditBalanceNum|numFormat}}</td>
            <td align="right">{{item.endingCreditBalance|numFormat}}</td>
          </tr>
          <tr v-if="!dataList.length">
            <td colspan="14" class="text-center padding">暂无数据</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </app-content>
</template>

<script>
import * as XLSX from "xlsx-js-style";
export default {
  name: "SubjectBalance",
  filters: {
    numFormat(val) {
      if (val === null || val === undefined || val === '') return '';
      const n = Number(val);
      return isNaN(n) ? '' : n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  },
  data() {
    return {
      dataList: [],
      accountDate: null,
      showNumPrice: false,
      loading: false,
      subjectList: [],
      startDate: null,
      endDate: null,
      formattedStartDate: null,
      formattedEndDate: null,
      subject: null,
      subjectId: null,
      numPriceDisabled: true,
      param: {
        keyName: 'id',
        parentName: 'parentId',
        titleName: 'subjectCodeName',
        dataMode: 'list',
        datas: []
      },
    }
  },
  watch: {
    subject() {
      let org = this.showNumPrice;
      if (this.subject && this.subject.unit) {
        this.numPriceDisabled = false;
        this.showNumPrice = true;
      } else {
        this.showNumPrice = false;
      }
      this.loadList();
    },
    showNumPrice() {
      this.loadList();
    }
  },
  methods: {
    formatDateToFirstDay(monthStr) {
      const [year, month] = monthStr.split('-').map(Number);
      const firstDay = new Date(year, month - 1, 1);
      return `${year}-${String(month).padStart(2, '0')}-01`;
    },

    handleDateChange({ startMonth, endMonth }) {
      if (startMonth) {
        this.formattedStartDate = this.formatDateToFirstDay(startMonth);
        const [year, month] = startMonth.split('-').map(Number);
        this.startDate = new Date(year, month - 1, 1);
      }

      if (endMonth) {
        this.formattedEndDate = this.formatDateToFirstDay(endMonth);
        const [year, month] = endMonth.split('-').map(Number);
        this.endDate = new Date(year, month - 1, 1);
      }
    },
    handleConfirm(params) {
      this.formattedStartDate = this.formatDateToFirstDay(params.startMonth);
      this.formattedEndDate = this.formatDateToFirstDay(params.endMonth);

      if (params.subject) {
        this.subject = params.subject;
        this.subjectId = params.subject.id;
      } else {
        this.subject = null;
        this.subjectId = null;
      }
      this.loadList();
    },
    async loadSubject() {
      this.dataList = [];
      try {
        const { data } = await this.$api.accountbook.loadSubject({ accountDate: this.accountDate });
        const formattedData = data.map(subject => ({
          id: subject.id,
          subjectCode: subject.code,
          subjectName: subject.name,
          subjectCodeName: subject.code + " " + subject.name,
          subjectFullName: subject.subjectFullName || subject.name,
          ...subject
        }));
        this.subjectList = formattedData;
        this.$set(this.param, "datas", formattedData);
        if (formattedData.length) {
          this.subjectId = formattedData[0].id;
          this.subject = formattedData[0];
        }
      } catch (error) {
        console.error('加载科目失败:', error);
      }
    },
    loadList() {
      this.loading = true;
      this.$api.accountbook.loadSubjectBalanceByDate({
        startDate: this.formattedStartDate,
        endDate: this.formattedEndDate,
        subjectId: this.subjectId,
        showNumPrice: this.showNumPrice,
      }).then(({ data }) => {
        this.dataList = data;
      }).catch(() => {
      }).finally(() => {
        this.loading = false;
      });
    },

    // 导出Excel 与明细账、总账统一实现方式
    exportExcel() {
      if (!this.dataList || this.dataList.length === 0) {
        this.$Message.warning("暂无科目余额数据，无法导出");
        return;
      }
      const sheetData = [];
      sheetData.push(["科目余额表"]);
      sheetData.push([]);

      if (!this.showNumPrice) {
        // 普通科目余额表双层表头
        sheetData.push([
          "科目编码", "科目名称",
          "期初余额", "",
          "本期发生额", "",
          "期末余额", ""
        ]);
        sheetData.push([
          "", "",
          "借方", "贷方",
          "借方", "贷方",
          "借方", "贷方"
        ]);

        this.dataList.forEach(item => {
          const nameIndent = "　".repeat(item.level || 0);
          sheetData.push([
            item.code || "",
            nameIndent + (item.name || ""),
            this.$options.filters.numFormat(item.beginningDebitBalance),
            this.$options.filters.numFormat(item.beginningCreditBalance),
            this.$options.filters.numFormat(item.currentDebitAmount),
            this.$options.filters.numFormat(item.currentCreditAmount),
            this.$options.filters.numFormat(item.endingDebitBalance),
            this.$options.filters.numFormat(item.endingCreditBalance)
          ]);
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        ws["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
          { s: { r: 2, c: 2 }, e: { r: 2, c: 3 } },
          { s: { r: 2, c: 4 }, e: { r: 2, c: 5 } },
          { s: { r: 2, c: 6 }, e: { r: 2, c: 7 } }
        ];
        ws["!cols"] = [
          { wch: 14 }, { wch: 24 },
          { wch: 14 }, { wch: 14 },
          { wch: 14 }, { wch: 14 },
          { wch: 14 }, { wch: 14 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, "科目余额表");
        XLSX.writeFile(wb, `科目余额表_${this.formattedStartDate}_${this.formattedEndDate}.xlsx`);
      } else {
        // 数量金额式双层表头
        sheetData.push([
          "科目编码", "科目名称", "计量单位",
          "期初余额", "", "", "",
          "本期发生额", "", "", "",
          "期末余额", "", "", ""
        ]);
        sheetData.push([
          "", "", "",
          "借方数量", "借方金额", "贷方数量", "贷方金额",
          "借方数量", "借方金额", "贷方数量", "贷方金额",
          "借方数量", "借方金额", "贷方数量", "贷方金额"
        ]);

        this.dataList.forEach(item => {
          const nameIndent = "　".repeat(item.level || 0);
          sheetData.push([
            item.code || "",
            nameIndent + (item.name || ""),
            item.unit || "-",
            "",
            this.$options.filters.numFormat(item.beginningDebitBalance),
            "",
            this.$options.filters.numFormat(item.beginningCreditBalance),
            this.$options.filters.numFormat(item.currentDebitAmountNum),
            this.$options.filters.numFormat(item.currentDebitAmount),
            this.$options.filters.numFormat(item.currentCreditAmountNum),
            this.$options.filters.numFormat(item.currentCreditAmount),
            this.$options.filters.numFormat(item.endingDebitBalanceNum),
            this.$options.filters.numFormat(item.endingDebitBalance),
            this.$options.filters.numFormat(item.endingCreditBalanceNum),
            this.$options.filters.numFormat(item.endingCreditBalance)
          ]);
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        ws["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 14 } },
          { s: { r: 2, c: 3 }, e: { r: 2, c: 6 } },
          { s: { r: 2, c: 7 }, e: { r: 2, c: 10 } },
          { s: { r: 2, c: 11 }, e: { r: 2, c: 14 } }
        ];
        ws["!cols"] = [
          { wch: 14 }, { wch: 24 }, { wch: 12 },
          { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
          { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
          { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, "科目余额表");
        XLSX.writeFile(wb, `科目余额表(数量金额)_${this.formattedStartDate}_${this.formattedEndDate}.xlsx`);
      }
      this.$Message.success("导出完成");
    }
  },
  created() {
    this.loadSubject();
  }
}
</script>

<style scoped lang="less">
/* 完全模仿科目汇总的样式风格，仅保留科目余额表的特殊样式 */
.balance {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0; /* 消除单元格间隙，避免双重边框 */

  td {
    padding: 0 8px;
    border: 1px solid #e2e2e2;
    /* 关键优化：取消单元格右侧和底部边框，实现单边框效果（与科目汇总一致） */
    border-right: none;
    border-bottom: none;
    font-size: 12px;
    height: 32px;

    &.tip {
      background-color: #FFEEEF;
    }
  }

  /* 给最后一列恢复右侧边框（与科目汇总一致） */
  tr td:last-child {
    border-right: 1px solid #e2e2e2;
  }

  /* 给最后一行恢复底部边框（与科目汇总一致） */
  tbody tr:last-child td {
    border-bottom: 1px solid #e2e2e2;
  }

  tbody tr:nth-child(even) {
    background-color: #f8fbf8;
  }

  tbody tr:hover {
    background-color: #F0F6FF;
  }

  .header {
    td {
      background-color: #F5F5F5;
      text-align: center;
      font-weight: bold;
      height: 35px; /* 与科目汇总表头高度一致 */
      /* 表头最后一列恢复右侧边框 */
      &:last-child {
        border-right: 1px solid #e2e2e2;
      }
    }
  }
}

/* 辅助样式：完全复用科目汇总的辅助样式 */
.text-center {
  text-align: center;
}

.padding {
  padding: 10px 0;
}

.text-hover {
  cursor: pointer;
}

.float-right {
  float: right;
}

.margin-right-left {
  margin: 0 10px;
}

.margin-top {
  margin-top: 10px;
}
</style>